import { cacheAllModuleFiles, updateModuleCache } from '@/store/sync/cachingUtils';
import api from './api';
import moduleIndex from './index.js';
import MODULE_ID from './moduleId';
import { SYNC_STATES } from '@/store/sync/sync';
import dataUtils from '@/common/dataUtils';
import dateUtils from '@/common/dateUtils';
import utils from '@/common/utils';
import socketio from '@/plugins/socketio';
import messagingUtil from './messagingUtil';
import sortFunctions from '@/sorters/sortFunctions';
import dateSorters from '@/sorters/dateSorters';

const DEBUG = false;
const ROOT = { root: true };

let getConnectedUsersInterval;

function getMessagingSendboxFromLocalStorage() {
  return JSON.parse(localStorage.getItem('messaging/sendbox') || '[]');
}

function storeMessagingSendboxInLocalStorage(messageingSendbox) {
  localStorage.setItem('messaging/sendbox', JSON.stringify(messageingSendbox));
}

function addMessagingSendboxItemToLocalStorage(action, payload) {
  let messageingSendbox = getMessagingSendboxFromLocalStorage();
  messageingSendbox.push({ action, payload });
  storeMessagingSendboxInLocalStorage(messageingSendbox);
}

function countUnreadMessages(state) {
  return countUnreadMessagesInArray(state.messages, state.userId);
}

function countUnreadMessagesInArray(messages, userId) {
  let unreadMessages = messages.filter((m) => messagingUtil.isUnread(m, userId));
  return unreadMessages.length;
}

const state = {
  // loaded from server
  userId: null,
  messages: [],
  groups: [],
  groupUserMapping: [],
  users: [],

  // processed data
  messagesByUserId: {},
  messagesByGroupId: {},
  groupById: {},
  messagesByChat: [],
  userById: {},

  // view states
  receivedMessage: null, //set when a new message receives and used to update count
  selectedReceiver: null,

  messagesByUserIdUnreadCount: 0,
  messagesByGroupIdUnreadCount: 0,

  openChat: null,
  dataLoaded: false,
  hasJoinedSocketRoom: false,
  selectedMessage: null,
  unreadCount: 0,
};

const getters = {
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    return state.unreadCount;
  },
};

const mutations = {
  setData(state, serverData) {
    state.userId = serverData.userId;
    state.userById = serverData.userById;
    state.messages = serverData.messages;
    state.users = serverData.users;
    state.messagesByUserId = serverData.messagesByUserId;
    state.messagesByGroupId = serverData.messagesByGroupId;
    state.groups = serverData.groups;
    state.groupById = serverData.groupById;
    state.groupUserMapping = serverData.groupUserMapping;
    state.messagesByUserIdUnreadCount = serverData.messagesByUserIdUnreadCount;
    state.messagesByGroupIdUnreadCount = serverData.messagesByGroupIdUnreadCount;
    state.dataLoaded = true;
  },
  setSelectedReceiver(state, selectedReveiver) {
    state.selectedReceiver = selectedReveiver;
  },
  setMessagesByUserIdUnreadCount(state, { userId, unreadCount }) {
    console.debug('setMessagesByUserIdUnreadCount', userId, unreadCount);
    state.messagesByUserIdUnreadCount[userId] = unreadCount;
    state.unreadCount = countUnreadMessages(state);
  },
  setMessagesByGroupIdUnreadCount(state, { groupId, unreadCount }) {
    console.debug('setMessagesByGroupIdUnreadCount', groupId, unreadCount);
    state.messagesByGroupIdUnreadCount[groupId] = unreadCount;
    state.unreadCount = countUnreadMessages(state);
  },
  // markMessageAsUnreadInNextSync(state, {uuid}) {
  //     state.markMessageAsUnreadInNextSyncUuid = uuid;
  // },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    state.unreadCount = countUnreadMessages(state);
    console.debug('messaging countModuleItems', state.unreadCount);
  },
  addMessage(state, message) {
    addMessagingSendboxItemToLocalStorage('SEND_MESSAGE', { message });
    state.messages.push(message);
  },
  initConnection(state, connected) {
    state.hasJoinedSocketRoom = connected;
  },

  onReceiveMessage(state, { message, returnValue = {} }) {
    console.debug(
      'messaging:on-receive-message store message in internal state (messagesByUserId for 1:1 messages, messagesByGroupId for group messages)',
      message
    );
    let messages;
    if (message.isGroup === 0) {
      messages = state.messagesByUserId[message.senderIdFk] || [];
      messages.push(message);
    } else {
      messages = state.messagesByGroupId[message.receiverIdFk] || [];
      console.debug('group messages existing', messages.length, JSON.stringify(messages, null, 2));
      // check if the same message is not received yet
      if (messages.find((m) => m.uuid === message.uuid)) {
        console.debug('message is stored already');
        returnValue.state = false;
        return;
      }
      messages.push(message);
      console.debug('group messages after push', messages.length, JSON.stringify(messages, null, 2));
    }
    //state.messages.push(message);
    state.messages = dateSorters.sortByDateIsoStringProperty(messages, 'datecreated');
    state.receivedMessage = message;

    let audio;
    audio = new Audio('/assets/audio/notification-chat-open.ogg'); //From https://material.io/design/sound/sound-resources.html
    //audio = new Audio('/assets/audio/notification-chat.ogg');
    try {
      audio
        .play()
        .then((r) => {
          console.debug('Chat received');
        })
        .catch((e) => {
          console.warn('Failed to play sound on message reception', e);
        });
    } catch (err) {
      console.warn('audio.play might have been failed because user did not interact with the doucment before', err);
    }
    returnValue.state = true;
  },

  onMessageMarkedAsRead(state, message) {
    console.debug('onMessageMarkedAsRead', message.uuid, message.dateread);
    let storedMessage = state.messages.find((m) => (message.id && m.id === message.id) || (message.clientSideId && m.clientSideId === message.clientSideId));
    if (storedMessage) {
      //console.debug('onMessageMarkedAsRead', storedMessage, storedMessage.dateread, message.dateread);
      if (!message.isBroadcast) {
        storedMessage.userIdFk = message.userIdFk;
      }

      storedMessage.dateread = message.dateread;
      state.unreadCount = countUnreadMessages(state);
    }
  },
  onMessageReceiptionConfirmed(state, message) {
    let storedMessage = state.messages.find((m) => m.uuid === message.uuid);
    console.debug('messaging:on-message-receiption-acknowledged', storedMessage, message.datereceived);
    if (storedMessage) {
      storedMessage.datereceived = message.datereceived;
    }
  },
  onMessageDeleteNotification(state, message) {
    // the message provided here is the record of the sender. This message will be found within the messagesByUserId[message.senderIdFk]
    let { messages, foundMessageIndex } = findInChatMessages(state, message, 'senderIdFk');

    console.debug('onMessageDeleteNotification foundMessageIndex', message, foundMessageIndex, messages);
    if (foundMessageIndex > -1 && !message.deleteMessageForAll) {
      messages.splice(foundMessageIndex, 1);
    }
    if (foundMessageIndex >= 0) {
      //preprocessMessage(message);
      messages[foundMessageIndex] = Object.assign(messages[foundMessageIndex], message);
    }
  },

  shareConnectedUsers(state, connectedUsers) {
    // console.debug('user:connected User has connected', state.userId, connectedUsers);
    state.users.forEach((user) => (user.online = false));
    if (Array.isArray(connectedUsers)) {
      connectedUsers.forEach((user) => {
        let storedUser = state.userById[user.userId];
        if (storedUser) {
          //console.debug('user is online', storedUser);
          storedUser.online = true;
        }
      });
    }
  },
};

/**
 *  Add a users array with the id's of the group members (userIdFk) to each group object
 *
 * @param groupUserMapping - mapping of groups and users (table messaging_user_group)
 * @param groupById - groups by their id
 */
function addUserIdArrayToGroups(groupUserMapping, groupById) {
  groupUserMapping.forEach((gu) => {
    let group = groupById[gu.groupIdFk];
    if (group) {
      let users = group.users;
      if (!users) {
        users = [];
        group.users = users;
      }
      users.push(gu.userIdFk);
    }
  });
}

/**
 * Assign group messages to the messagesByGroupId object and direct user messages to the messagesByUserId object.
 * @param messages
 * @param messagesByGroupId
 * @param messagesByUserId
 * @param userId
 */
function buildMessagesByObjects(messages, messagesByGroupId, messagesByUserId, userId) {
  messages.forEach((m, index, arr) => {
    if (m.isGroup) {
      if (!messagesByGroupId[m.receiverIdFk]) {
        messagesByGroupId[m.receiverIdFk] = [];
      }
      messagesByGroupId[m.receiverIdFk].push(m);
    } else {
      let addressee = m.senderIdFk === userId ? m.receiverIdFk : m.senderIdFk;
      if (!messagesByUserId[addressee]) {
        messagesByUserId[addressee] = [];
      }
      messagesByUserId[addressee].push(m);
    }
  });
}

/**
 * Sort the messages arrays by datecreated and count the unread messages
 * hey, this was an ugly bug: Used userId in the forEach loop, which did overwrite the userId param!!!!
 *
 * @param messagesByUserId
 * @param messagesByUserIdUnreadCount
 * @param userId - the id of the current user
 */
function sortAndCountUnreadMessagesByUserIdObject(messagesByUserId, messagesByUserIdUnreadCount, userId) {
  Object.keys(messagesByUserId).forEach((userId_) => {
    let messages = messagesByUserId[userId_].sort((c1, c2) => {
      return dateUtils.compareISOStringDates(c1.datecreated, c2.datecreated);
    });
    messagesByUserId[userId_] = messages;
    messagesByUserIdUnreadCount[userId_] = countUnreadMessagesInArray(messages, userId);
  });
}

/**
 * Sort messagesByGroupId and count for each group
 * @param messagesByGroupId
 * @param messagesByGroupIdUnreadCount
 * @param userId
 */
function sortAndCountUnreadMessagesByGroupIdObject(messagesByGroupId, messagesByGroupIdUnreadCount, userId) {
  Object.keys(messagesByGroupId).forEach((groupId) => {
    let messages = messagesByGroupId[groupId].sort((c1, c2) => {
      return dateUtils.compareISOStringDates(c1.datecreated, c2.datecreated);
    });
    messagesByGroupId[groupId] = messages;
    messagesByGroupIdUnreadCount[groupId] = countUnreadMessagesInArray(messages, userId);
    //console.debug('messagesByGroupIdUnreadCount[groupId] userId=', groupId, ':', messagesByGroupIdUnreadCount[groupId]);
  });
}

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ rootGetters, state }, { filterCriteria, options }) {
    let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({ moduleType: 'Messaging' });
    return;
    // NOT IMPLEMENTED currently. Reason: No routing onto the chats to display search results.
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    const { userId, userById, users, messages, messagesByUserId, messagesByGroupId, groupById, groups, groupUserMapping } = state;
    await updateModuleCache(moduleIndex.api, {
      userId,
      userById,
      users,
      messages,
      messagesByUserId,
      messagesByGroupId,
      groupById,
      groups,
      groupUserMapping,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    let files = state.messages
      .filter((m) => m.type === 'image' && m.deletedBySender === 0)
      .map((m) => {
        return { filename: m.filename, checksum: m.checksum };
      });

    await cacheAllModuleFiles({ files: files, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ state, commit, dispatch }) {
    DEBUG && console.debug('Action: synchronizeData');

    const appliedMessagingFix = localStorage.getItem('appliedMessagingFix_2024-08-19_4');
    if (!appliedMessagingFix) {
      console.debug('set messaging/sendbox to []');
      localStorage.setItem('messaging/sendbox', '[]');
      localStorage.setItem('appliedMessagingFix_2024-08-19_4', 'true');
    }

    try {
      await dispatch('registerSocketHandlers');
      await dispatch('sendMessagingSendbox');
      const response = await api.getData();
      let { userId, messages, groups, groupUserMapping, users } = response.data;
      userId = Number(userId);
      await dispatch('processMessagingData', { userId: userId, messages, groups, groupUserMapping, users });
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { MODULE_ID, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
  /**
   * Preprocess the messaging data (as provided by the backend service) and store into the state
   * @returns an Object of preprocessed data (usually not used because already set to the state)
   */
  async processMessagingData({ state, commit }, { userId, messages, groups = [], groupUserMapping = [], users }) {
    // set default online state - will be changed by socket events
    users.forEach((user) => (user.online = false));
    let userById = dataUtils.buildIdMap(users, 'id');
    let groupById = dataUtils.buildIdMap(groups, 'id');

    // initialize
    groups.forEach((g) => (g.users = []));

    // Add a users array with the id's of the group members (userIdFk) to each group object
    addUserIdArrayToGroups(groupUserMapping, groupById);

    // filter groups where user is not part of
    groups = groups ? groups.filter((g) => g.users.indexOf(userId) > -1) : [];

    // get the list of group ids
    let groupIds = groups.map((g) => g.id);

    let messagesByUserId = {};
    let messagesByGroupId = {};
    const messagesByUserIdUnreadCount = {};
    const messagesByGroupIdUnreadCount = {};

    users.forEach((u) => (messagesByUserId[u.id] = [])); // initialize for reactivity reasons
    groups.forEach((g) => (messagesByGroupId[g.id] = [])); // initialize for reactivity reasons

    // accept all messages within groups the user belongs to and all direct messages
    messages = messages.filter((m) => !m.isGroup || (m.isGroup && groupIds.indexOf(m.receiverIdFk) >= 0));

    buildMessagesByObjects(messages, messagesByGroupId, messagesByUserId, userId);

    // sort messagesByUserId and count unread for each user
    sortAndCountUnreadMessagesByUserIdObject(messagesByUserId, messagesByUserIdUnreadCount, userId);

    // sort messagesByGroupId and count for each group
    sortAndCountUnreadMessagesByGroupIdObject(messagesByGroupId, messagesByGroupIdUnreadCount, userId);

    commit('setData', {
      userId,
      userById,
      users,
      messages,
      //messagesById,
      messagesByUserId,
      messagesByGroupId,

      messagesByUserIdUnreadCount,
      messagesByGroupIdUnreadCount,

      groupById,
      groups,
      groupUserMapping,
    });

    return {
      userId,
      userById,
      users,
      messages,
      messagesByUserId,
      messagesByGroupId,
      groupById,
      groups,
      groupUserMapping,
    };
  },
  async registerSocketHandlers({ state, rootState, commit, dispatch }) {
    let { userId } = state;

    try {
      const socket = socketio.socket;

      console.debug('isHandler defined?', socket._callbacks);
      console.debug('socket connected', socket.id);
      let stage = rootState.configs.stage;
      let enableDisplayOfOnlineStateInMessaging = rootState.configs.features[`enableDisplayOfOnlineStateInMessaging_${stage}`];
      console.debug(`enableDisplayOfOnlineStateInMessaging stage=${stage} ${enableDisplayOfOnlineStateInMessaging}`);
      if (enableDisplayOfOnlineStateInMessaging) {
        if (getConnectedUsersInterval) clearInterval(getConnectedUsersInterval);
        const getConnectedUsers = async () => {
          try {
            console.debug('phase', rootState.lifecycle.phase);
            if (['ready', 'authenticated'].indexOf(rootState.lifecycle.phase) >= 0) {
              let connectedUsersResponse = await api.getConnectedUsers();
              commit('shareConnectedUsers', connectedUsersResponse.data.connectedUsers || []);
            }
          } catch (err) {
            console.warn('Failed to get other users', err);
          }
        };
        getConnectedUsersInterval = setInterval(getConnectedUsers, 60000); //check every minute
        await getConnectedUsers();
      }

      socket.removeAllListeners('user:connected');

      socket.on('user:connected', ({ tenant, username, userId, connectedUsers }) => {
        // console.debug('user:connected User has connected', tenant, username, userId, connectedUsers);
        commit('shareConnectedUsers', connectedUsers);
      });

      // message receiver
      socket.removeAllListeners('messaging:on-receive-message');

      socket.on('messaging:on-receive-message', async (message) => {
        message.datereceived = new Date().toISOString(); // set the datereceived first

        // Send the acknoweledgement immediately
        addMessagingSendboxItemToLocalStorage('ACKNOWLEDGE_MESSAGE_RECEPTION', { message });
        await dispatch('sendMessagingSendbox');

        let returnValue = {};
        commit('onReceiveMessage', { message, returnValue });
        if (returnValue.state === false) {
          console.debug('message is already in store, ignore all other operations');
          return;
        }

        const isMessagingRoute = window.location.pathname.includes('messaging');
        if (message.isGroup === 0) {
          if (isMessagingRoute && state.selectedReceiver && state.selectedReceiver.userId && state.selectedReceiver.userId === message.senderIdFk) {
            console.debug('If a group message arrives and the the user has this very chat open, mark the message as read after x seconds');

            setTimeout(() => {
              dispatch('markMessageAsRead', message);
              state.messagesByUserIdUnreadCount[message.senderIdFk] = Math.max(0, state.messagesByUserIdUnreadCount[message.senderIdFk] - 1);
              state.unreadCount = countUnreadMessages(state);
            }, 3000);
          }
          // Increase counter of this sender
          state.messagesByUserIdUnreadCount[message.senderIdFk] = 1 + (state.messagesByUserIdUnreadCount[message.senderIdFk] || 0);
        } else {
          if (isMessagingRoute && state.selectedReceiver && state.selectedReceiver.groupId && state.selectedReceiver.groupId === message.receiverIdFk) {
            console.debug('mark as read since displayed');
          }
          // Increase counter of this sender
          state.messagesByGroupIdUnreadCount[message.receiverIdFk] = 1 + (state.messagesByGroupIdUnreadCount[message.receiverIdFk] || 0);
        }
        state.unreadCount = state.unreadCount + 1;

        dispatch('sync/getAndSetAppCounter', {}, { root: true });
      });

      // message receiption confirmed
      socket.removeAllListeners('messaging:on-message-receiption-acknowledged');
      socket.on('messaging:on-message-receiption-acknowledged', async (message) => {
        commit('onMessageReceiptionConfirmed', message);
      });

      // message delete notification
      socket.removeAllListeners('messaging:on-message-deleted');
      socket.on('messaging:on-message-deleted', (message) => {
        commit('onMessageDeleteNotification', message);
      });

      console.debug('isHandler defined?', socket._callbacks['$messaging:on-receive-message'] !== undefined);

      socket.removeAllListeners('messaging:on-message-marked-as-read');
      socket.on('messaging:on-message-marked-as-read', (message) => {
        commit('onMessageMarkedAsRead', message);
      });
    } catch (err) {
      //
    }
  },

  /**
   * The sendChatMessage function has the responsibility to prepare and send a chat message. However the message is
   * not really sent but stored only into the sendbox. THe sendbox is sent later-on.
   * @param {*} param0
   * @param {*} message the message object
   */
  async sendChatMessage({ state, rootState, commit, dispatch }, { receiver, text, base64Image, file }) {
    console.debug('Store new message to cache storage and initiate sync');
    let receiverIdFk = receiver.groupId ? receiver.groupId : receiver.userId;
    let groupId = receiver.groupId;
    let now = new Date().toISOString();

    let fileBase64 = null;
    let fileName = null;
    let fileType = null;
    let messageType = 'text'; // Default type

    if (base64Image) {
        messageType = 'image';
    } else if (file) {
        // convert the file to Base64 (a data URL) to store it in the sendbox,
        try {
            fileBase64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
            fileName = file.name;
            fileType = file.type;
            messageType = 'document';
        } catch (e) {
            console.error('Failed to read file for sending:', e);
            return;
        }
    }
    let message = {
      userIdFk: groupId ? state.userId : receiverIdFk,
      receiverIdFk,
      senderIdFk: state.userId,
      isGroup: receiver.groupId ? 1 : 0,
      text,
      uuid: null,
      forwarded: 0,
      deleted: 0,
      deletedBySender: 0,
      dateread: null, // crucial to set to null otherwise no reactivity
      datereceived: null, // crucial to set to null otherwise no reactivity
      datecreated: now,
      dateupdated: now,
      userdatemodified: now,
      type: messageType,                 // 'text', 'image', or 'file'
      base64Image: base64Image,          // Will be null if it's a file
      fileBase64: fileBase64,
      fileName: fileName,
      fileType: fileType,
      sendStatus: 'NOT_SENT',
      clientSideId: utils.getRandomString(),
    };

    // Update state
    commit('addMessage', message);

    await dispatch('sendMessagingSendbox');

    let { userId, groups, groupUserMapping, users } = state;
    await dispatch('processMessagingData', { userId, messages: state.messages, groups, groupUserMapping, users });

    await dispatch('updateModuleApplicationCache');
  },
  markMessageAsRead({ state, commit, dispatch }, message) {
    if (message.dateread) return; // ignore because message is read

    console.debug('markMessageAsRead', message);
    message.dateread = new Date().toISOString();
    if (message.isCockpitMsg) delete message.isCockpitMsg;
    addMessagingSendboxItemToLocalStorage('markMessageAsRead', { message });

    commit('countModuleItems');
    dispatch('updateModuleApplicationCache');
    dispatch('sendMessagingSendbox');
  },
  /**
   * Initiate a deletion of the message
   * @param {Object} message
   * @param {String} mode - deleteMessageForAll or deleteMessageForMe
   */
  deleteMessage({ state, dispatch }, { message, mode }) {
    console.debug('deleteMessage', message);
    let now = new Date().toISOString();
    message.datedeleted = now;

    // find in messages listed in messagesByUserId[message.receiverIdFk] in case of a direct message
    let { messages, foundMessageIndex } = findInChatMessages(state, message, 'receiverIdFk');

    const handleMessage = (foundMessageIndex) => {
      if (foundMessageIndex) {
        console.debug('messages', JSON.stringify(messages, null, 2));
        console.debug('foundMessage', JSON.stringify(messages[foundMessageIndex], null, 2));
        if (mode === 'deleteMessageForAll') {
          message.deleteMessageForAll = true; // show as deleted
          message.deletedBySender = 1;
        } else {
          message.deleteMessageForMe = true;
          console.debug('messages', messages);
          console.debug('foundMessageIndex', foundMessageIndex);
          messages.splice(foundMessageIndex, 1);
        }
      }
    };
    handleMessage(foundMessageIndex);

    addMessagingSendboxItemToLocalStorage('deleteMessage', { message });

    // updateCacheStorage(state);
    dispatch('updateModuleApplicationCache');
    dispatch('sendMessagingSendbox');
  },
  /**
   * Delete a complete chat by removing all messages of this chat
   * @param {*} param0
   * @param {Object} receiver - the receiver object used in MessagingDrawer. It contains  the receiver information and a messages array.
   */
  async deleteChat({ state, commit, dispatch }, receiver) {
    console.debug('delete chat of receiver', receiver);
    let messagesToDeleteIds = receiver.messages.map((m) => m.id);
    console.debug('messagesToDeleteIds', messagesToDeleteIds);
    if (receiver.groupId) {
      console.debug('Delete group with groupId=receiverId', receiver.groupId);
    } else {
      console.debug('Delete chat with receiverId', receiver.receiverId, 'sender', receiver.senderId);
    }
    console.debug('messages count before removing', state.messages.length);
    // HINT: Do not splice within the loop because the function loops over the array as well (two loops results in O(n^2) complexity)
    state.messages = state.messages.filter((m) => messagesToDeleteIds.indexOf(m.id) < 0);

    console.debug('messages count AFTER removing', state.messages.length);

    let { userId, messages, groups, groupUserMapping, users } = state;
    await dispatch('processMessagingData', { userId, messages, groups, groupUserMapping, users });

    addMessagingSendboxItemToLocalStorage('deleteChat', {
      groupId: receiver.groupId,
      receiverId: receiver.receiverId,
      senderId: receiver.senderId,
    });

    dispatch('updateModuleApplicationCache');
    dispatch('sendMessagingSendbox');
  },
  async sendMessagingSendbox({ state, dispatch }) {
    let messageingSendbox = getMessagingSendboxFromLocalStorage();
    console.debug('sendMessagingSendbox', messageingSendbox);
    if (navigator.onLine) {
      try {
        let response = await api.sendMessagingAction(messageingSendbox);
        if (response && response.status === 200 && response.data) {
          console.debug('sendMessagingSendbox response status', response.status, response.statusText);

          messageingSendbox = [];
          storeMessagingSendboxInLocalStorage(messageingSendbox);

          // update mutated messages
          // console.debug('sendbox response', JSON.stringify(response.data, null, 2));
          response.data.forEach((item) => {
            let { action, message } = item;
            if (action === 'SEND_MESSAGE') {
              state.messages
                .filter((m) => m.clientSideId === message.clientSideId)
                .forEach((m) => {
                  console.debug(`Stored message with clientSideId=${m.clientSideId} found. Add server side generated uuid=${message.uuid}`);

                  m.uuid = message.uuid;
                  m.userIdFk = message.userIdFk;
                  m.fileName = message.fileName;
                  m.checksum = message.checksum;
                });
            }
          });
        }
      } catch (err) {
        console.error('failed to send messagingSendbox', err);
      }
    }
  },
};

/**
 * Find the message within state.messagesByGroupId (if group message) or within state.messagesByUserId
 * @param {*} state the state
 * @param {*} message the message to finde
 * @returns an Object of messages (either from messagesByGroupId or from messagesByUserId) and foundMessageIndex (the index with the messages array)
 */
function findInChatMessages(state, message, messageIdAttribute) {
  let foundMessageIndex;
  let messages;
  if (message.isGroup) {
    messages = state.messagesByGroupId[message.receiverIdFk];
    if (messages) {
      foundMessageIndex = findMessageIndex(message, messages);
    }
  } else {
    messages = state.messagesByUserId[message[messageIdAttribute]];
    if (messages) {
      foundMessageIndex = findMessageIndex(message, messages);
    }
  }
  return { messages, foundMessageIndex };
}

function findMessageIndex(message, messages) {
  return messages.findIndex((m) => m.uuid === message.uuid);
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
